import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { utils, writeFileXLSX } from 'xlsx'
import { ArrowLeft, User, Receipt, Trash2, FileSpreadsheet, Ticket, UserPlus } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RevenuesService, type Revenue, type RevenueStatement, type RevenueCommission } from '@/services/modules/RevenuesService'
import { useAuthority } from '@/hooks/useAuthority'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { brl, dateBR } from '@/lib/format'
import { exportToXlsx } from '@/lib/exportXlsx'

export function RevenueDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // Cada ação do extrato tem a própria permissão no backend. `useAuthority` já
  // nega tudo para EMPRESA/parceiros (rotas do painel admin).
  const { can } = useAuthority()
  const [revenue, setRevenue] = useState<Revenue | null>(null)
  const [loading, setLoading] = useState(true)
  const [toDelete, setToDelete] = useState<RevenueStatement | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [reapplyOpen, setReapplyOpen] = useState(false)
  const [reapplying, setReapplying] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [addable, setAddable] = useState<{ id: string; name: string; planName: string | null }[]>([])
  const [addableLoading, setAddableLoading] = useState(false)
  const [addSubscriberId, setAddSubscriberId] = useState('')
  const [adding, setAdding] = useState(false)
  const [stmtPage, setStmtPage] = useState(1)
  const [stmtSearch, setStmtSearch] = useState('')
  const STMT_PAGE_SIZE = 25

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    RevenuesService.show(id)
      .then((r) => setRevenue(r))
      .catch(() => setRevenue(null))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => { load() }, [load])

  if (loading) return <div className="text-sm text-gray-500">Carregando</div>
  if (!revenue) return <div className="text-sm text-gray-500">Fatura não encontrada.</div>

  // Cópia não-nula para uso dentro de closures (export/onClick) sem reabrir o narrowing.
  const rev = revenue

  const customerHref = revenue.subscriberId
    ? `/commercial/subscribers/${revenue.subscriberId}/editar`
    : revenue.companyId
      ? `/commercial/companies/${revenue.companyId}/editar`
      : null
  const customerName = revenue.subscriber?.name ?? revenue.company?.name ?? '—'
  const isBoleto = (revenue.method ?? '').toUpperCase() === 'BOLETO'

  const statements = revenue.statements ?? []
  // Excluir item de extrato — apenas em faturas PENDENTES.
  const canDeleteStatement = revenue.status === 'PENDENTE' && can('financial.revenues.delete_statement')
  // Reaplicar cupons — só faz sentido em faturas pendentes. Não aparece na visão
  // da empresa (é rota do painel admin, bloqueada por admin.panel).
  const canReapplyCoupons = revenue.status === 'PENDENTE' && can('financial.revenues.reapply_coupons')
  // Adicionar funcionário — só em faturas de EMPRESA pendentes (função da administração).
  const canAddCollaborator = !!revenue.companyId && revenue.status === 'PENDENTE' && can('financial.revenues.edit_statement')

  // Totais do extrato (sobre TODOS os itens, não apenas a página visível)
  const subtotal = statements.reduce((acc, s) => acc + (s.planAmount || 0), 0)
  const discounts = statements.reduce((acc, s) => acc + (s.discountAmount || 0), 0)
  const total = statements.reduce((acc, s) => acc + (s.amount || 0), 0)

  // Extrato ordenado por nome do assinante (ignora caixa/acento) + filtro por nome.
  const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const sortedStatements = [...statements].sort((a, b) =>
    normalize(a.subscriber?.name ?? '').localeCompare(normalize(b.subscriber?.name ?? '')),
  )
  const filteredStatements = stmtSearch.trim()
    ? sortedStatements.filter((s) => normalize(s.subscriber?.name ?? '').includes(normalize(stmtSearch)))
    : sortedStatements

  // Paginação client-side do extrato (faturas de empresa têm milhares de itens)
  const stmtTotalPages = Math.max(1, Math.ceil(filteredStatements.length / STMT_PAGE_SIZE))
  const stmtPageSafe = Math.min(stmtPage, stmtTotalPages)
  const pagedStatements = filteredStatements.slice((stmtPageSafe - 1) * STMT_PAGE_SIZE, stmtPageSafe * STMT_PAGE_SIZE)

  async function confirmReapplyCoupons() {
    setReapplying(true)
    try {
      await RevenuesService.reapplyCoupons(rev.id)
      toast.success({ title: 'Cupons reaplicados', description: 'O extrato e o total da fatura foram recalculados.' })
      setReapplyOpen(false)
      load()
    } catch (e) {
      toast.error({ title: 'Não foi possível reaplicar', description: extractApiError(e).message })
    } finally {
      setReapplying(false)
    }
  }

  async function openAddCollaborator() {
    setAddSubscriberId('')
    setAddOpen(true)
    setAddableLoading(true)
    try {
      const list = await RevenuesService.addableCollaborators(rev.id)
      setAddable(list)
    } catch (e) {
      toast.error({ title: 'Não foi possível carregar', description: extractApiError(e).message })
      setAddOpen(false)
    } finally {
      setAddableLoading(false)
    }
  }

  async function confirmAddCollaborator() {
    if (!addSubscriberId) return
    setAdding(true)
    try {
      await RevenuesService.addStatement(rev.id, addSubscriberId)
      const added = addable.find((a) => a.id === addSubscriberId)?.name ?? 'Funcionário'
      toast.success({ title: 'Funcionário adicionado', description: `${added} entrou no extrato. O total foi recalculado.` })
      setAddOpen(false)
      load()
    } catch (e) {
      toast.error({ title: 'Não foi possível adicionar', description: extractApiError(e).message })
    } finally {
      setAdding(false)
    }
  }

  async function handleDeleteStatement() {
    if (!toDelete) return
    setDeleting(true)
    try {
      await RevenuesService.deleteStatement(toDelete.id)
      toast.success({ title: 'Item removido', description: 'O item do extrato foi excluído.' })
      setToDelete(null)
      load()
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    } finally {
      setDeleting(false)
    }
  }

  // % de desconto sobre o valor do plano do item.
  const discountPct = (s: RevenueStatement) =>
    s.planAmount > 0 ? (s.discountAmount / s.planAmount) * 100 : 0

  /**
   * Quem é o dono da mensalidade.
   *
   * Um travessão aqui escondia duas situações diferentes: o assinante excluído
   * depois da fatura (o dado existe) e a linha que perdeu o vínculo de vez (o
   * valor continua na fatura, sem dono). Nos dois casos o operador precisa
   * saber que não é falha de exibição.
   */
  const statementOwner = (s: RevenueStatement) => {
    if (s.subscriber?.name) {
      return s.subscriberRemoved ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-gray-500 line-through">{s.subscriber.name}</span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">excluído</span>
        </span>
      ) : s.subscriber.name
    }
    if (s.subscriberFallbackName) {
      return (
        <span
          className="inline-flex items-center gap-1.5"
          title="O cadastro deste assinante não existe mais. O nome vem do registro da importação."
        >
          <span className="text-gray-500">{s.subscriberFallbackName}</span>
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">sem cadastro</span>
        </span>
      )
    }
    return (
      <span
        className="text-amber-700"
        title="O assinante desta linha não existe mais no sistema. O valor continua compondo a fatura."
      >
        Assinante não identificado
      </span>
    )
  }

  const stmtColumns: DataTableColumn<RevenueStatement>[] = [
    { key: 'sequence',   header: 'Parcela', align: 'center', render: (s) => s.sequence === 9999 ? '—' : `#${s.sequence}` },
    { key: 'subscriber', header: 'Assinante', render: (s) => statementOwner(s) },
    { key: 'coupon',     header: 'Cupom', align: 'center', render: (s) => s.couponCode ?? '—' },
    { key: 'plan',       header: 'Plano', align: 'right', render: (s) => s.formatted?.planAmount ?? brl(s.planAmount) },
    { key: 'discount',   header: 'Desconto', align: 'right', render: (s) => (s.discountAmount > 0 ? `- ${s.formatted?.discountAmount ?? brl(s.discountAmount)}` : '—') },
    { key: 'discountPct', header: 'Desc. %', align: 'right', render: (s) => (s.discountAmount > 0 ? `${discountPct(s).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : '—') },
    { key: 'amount',     header: 'Total', align: 'right', render: (s) => <span className="font-medium text-gray-800">{s.formatted?.amount ?? brl(s.amount)}</span> },
  ]

  // Exporta o extrato (ordenado + filtrado) para .xlsx.
  function exportStatements() {
    if (filteredStatements.length === 0) {
      toast.error({ title: 'Nada para exportar', description: 'O extrato está vazio.' })
      return
    }
    const aoa = [
      ['Parcela', 'Assinante', 'Cupom', 'Plano', 'Desconto', 'Desc. %', 'Total'],
      ...filteredStatements.map((s) => [
        s.sequence === 9999 ? '' : s.sequence,
        s.subscriber?.name
          ? (s.subscriberRemoved ? `${s.subscriber.name} (excluído)` : s.subscriber.name)
          : (s.subscriberFallbackName ? `${s.subscriberFallbackName} (sem cadastro)` : 'Assinante não identificado'),
        s.couponCode ?? '',
        s.planAmount,
        s.discountAmount,
        s.discountAmount > 0 ? Number(discountPct(s).toFixed(2)) : 0,
        s.amount,
      ]),
    ]
    const ws = utils.aoa_to_sheet(aoa)
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Extrato')
    writeFileXLSX(wb, `extrato-fatura-${rev.code}.xlsx`)
  }

  const commColumns: DataTableColumn<RevenueCommission>[] = [
    { key: 'user',   header: 'Usuário', render: (c) => c.user?.name ?? '—' },
    { key: 'type',   header: 'Tipo', align: 'center', render: (c) => c.type },
    { key: 'level',  header: 'Origem', align: 'center', render: (c) => (
      <span className={c.isFirstLevel ? 'text-emerald-700' : 'text-violet-700'}>
        {c.isFirstLevel ? 'Direto' : 'Rede'}
      </span>
    ) },
    { key: 'amount', header: 'Valor', align: 'right', render: (c) => c.formatted?.amount ?? brl(c.amount) },
  ]

  // Exporta as comissões da fatura para .xlsx (valor numérico, como no extrato).
  function exportCommissions() {
    const rows = rev.commissions ?? []
    if (rows.length === 0) {
      toast.error('Nenhum registro para exportar.')
      return
    }
    exportToXlsx(`comissoes-fatura-${rev.code}`, rows, [
      { header: 'Usuário',    accessor: (c) => c.user?.name ?? '' },
      { header: 'Tipo',       accessor: (c) => c.type },
      { header: 'Origem',     accessor: (c) => (c.isFirstLevel ? 'Direto' : 'Rede') },
      { header: 'Valor (R$)', accessor: (c) => c.amount },
    ])
  }

  return (
    <>
      <PageHeader
        title={`Fatura ${revenue.code}`}
        subtitle="Detalhes da fatura"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/revenues')} iconLeft={<ArrowLeft size={14} />}>Voltar</Button>
            {customerHref && (
              <Button variant="secondary" onClick={() => navigate(customerHref)} iconLeft={<User size={14} />}>Ver cliente</Button>
            )}
            {isBoleto && revenue.bankSlipUrl && (
              <Button onClick={() => window.open(revenue.bankSlipUrl!, '_blank', 'noopener,noreferrer')} iconLeft={<Receipt size={14} />}>
                Ver boleto
              </Button>
            )}
          </div>
        }
      />

      <Card className="mb-4">
        <CardHeader title="Resumo" />
        <CardBody>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
            <Field label="Cliente" value={customerName} />
            <Field label="Tipo" value={revenue.subscriberId ? 'Associado' : revenue.companyId ? 'Empresa' : '—'} />
            <Field label="Status" value={<StatusBadge status={revenue.status} />} />
            <Field label="Valor" value={revenue.formatted?.amount ?? brl(revenue.amount)} />
            <Field label="Vencimento" value={revenue.formatted?.dueDate ?? dateBR(revenue.dueDate)} />
            <Field label="Pago em" value={revenue.formatted?.paidDate ?? '—'} />
            <Field label="Método" value={revenue.method ?? '—'} />
            <Field label="Gateway" value={revenue.gateway ?? '—'} />
            <Field label="Criada em" value={revenue.formatted?.createdAt ?? '—'} />
          </dl>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader
          title="Extrato"
          subtitle="Itens que compõem o valor desta fatura"
          actions={
            <Button variant="secondary" size="sm" onClick={exportStatements} iconLeft={<FileSpreadsheet size={14} />}>
              Exportar Excel
            </Button>
          }
        />
        <CardBody>
          {(statements.length > 1 || canReapplyCoupons || canAddCollaborator) && (
            <div className="mb-3 flex items-center gap-2">
              {statements.length > 1 && (
                <div className="max-w-xs flex-1">
                  <Input
                    placeholder="Filtrar por nome"
                    value={stmtSearch}
                    onChange={(e) => { setStmtSearch(e.target.value); setStmtPage(1) }}
                  />
                </div>
              )}
              {canAddCollaborator && (
                <Button variant="secondary" size="sm" onClick={openAddCollaborator} iconLeft={<UserPlus size={14} />}>
                  Adicionar funcionário
                </Button>
              )}
              {canReapplyCoupons && (
                <Button variant="secondary" size="sm" onClick={() => setReapplyOpen(true)} iconLeft={<Ticket size={14} />}>
                  Reaplicar cupons
                </Button>
              )}
            </div>
          )}

          <DataTable
            columns={stmtColumns}
            rows={pagedStatements}
            getRowKey={(s) => s.id}
            emptyMessage="Nenhum item de extrato."
            rowActions={canDeleteStatement ? (s) => [
              { key: 'delete', label: 'Excluir item', icon: <Trash2 size={14} />, danger: true, onClick: () => setToDelete(s) },
            ] : undefined}
          />

          {/* Paginação simples do extrato */}
          {filteredStatements.length > STMT_PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
              <span>
                {(stmtPageSafe - 1) * STMT_PAGE_SIZE + 1}–{Math.min(stmtPageSafe * STMT_PAGE_SIZE, filteredStatements.length)} de {filteredStatements.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={stmtPageSafe <= 1} onClick={() => setStmtPage((p) => Math.max(1, p - 1))}>Anterior</Button>
                <span className="text-xs text-gray-500">Página {stmtPageSafe} de {stmtTotalPages}</span>
                <Button variant="secondary" size="sm" disabled={stmtPageSafe >= stmtTotalPages} onClick={() => setStmtPage((p) => Math.min(stmtTotalPages, p + 1))}>Próxima</Button>
              </div>
            </div>
          )}

          {/* Totais do extrato */}
          <div className="mt-8 flex justify-end border-t border-gray-100 pt-5">
            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <dt>Subtotal</dt>
                <dd>{brl(subtotal)}</dd>
              </div>
              <div className="flex justify-between text-gray-600">
                <dt>Descontos</dt>
                <dd className={discounts > 0 ? 'text-rose-600' : ''}>{discounts > 0 ? `- ${brl(discounts)}` : brl(0)}</dd>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5 font-semibold text-gray-800">
                <dt>Total</dt>
                <dd>{brl(total)}</dd>
              </div>
            </dl>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Comissões" subtitle="Direto (quem fechou) e rede (upline)" />
        <CardBody>
          <DataTable
            columns={commColumns}
            rows={revenue.commissions ?? []}
            getRowKey={(c) => c.id}
            exportable
            onExportAll={exportCommissions}
            emptyMessage="Nenhuma comissão registrada."
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDeleteStatement}
        loading={deleting}
        title="Excluir item do extrato?"
        description={`Remover "${toDelete?.subscriber?.name ?? 'item'}" (${toDelete ? brl(toDelete.amount) : ''}) desta fatura? O valor total será recalculado.`}
        confirmLabel="Excluir"
        variant="danger"
      />

      <ConfirmDialog
        open={reapplyOpen}
        onClose={() => setReapplyOpen(false)}
        onConfirm={confirmReapplyCoupons}
        loading={reapplying}
        title="Reaplicar cupons?"
        description="Recalcula os valores do extrato e o total desta fatura usando os cupons atuais dos assinantes. Não altera a cobrança no Asaas — apenas corrige os valores no sistema."
        confirmLabel="Reaplicar"
      />

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Adicionar funcionário ao extrato"
        footer={<>
          <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button onClick={confirmAddCollaborator} loading={adding} disabled={!addSubscriberId || addableLoading}>Adicionar</Button>
        </>}
      >
        <p className="text-sm text-gray-600 mb-3">
          Somente colaboradores <strong>{rev.company?.name ?? 'desta empresa'}</strong> ativos, com plano, que ainda não estão no extrato. O total da fatura é recalculado ao adicionar.
        </p>
        {addableLoading ? (
          <p className="text-sm text-gray-500">Carregando colaboradores</p>
        ) : addable.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum colaborador disponível — todos os ativos com plano já estão no extrato.</p>
        ) : (
          <Select
            label="Funcionário"
            required
            value={addSubscriberId}
            onChange={(e) => setAddSubscriberId(e.target.value)}
            options={addable.map((c) => ({ value: c.id, label: c.planName ? `${c.name} — ${c.planName}` : c.name }))}
            placeholder="Selecione um colaborador"
            searchPlaceholder="Buscar colaborador"
          />
        )}
      </Modal>
    </>
  )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-800">{value}</dd>
    </div>
  )
}

export default RevenueDetailPage
